import { useCallback, useRef, useMemo, useSyncExternalStore } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Email, EmailFolder } from '@/types/email';
import { toast } from 'sonner';

interface UseEmailManagerOptions {
  folder?: EmailFolder;
  limit?: number;
  accountId?: string;  // ← NOVO: ID da conta específica
  /** Força listagem via API Nylas (ignora cache Postgres na edge). */
  refreshFromNylas?: boolean;
  filters?: {
    search?: string;
    unread?: boolean | null;
    hasAttachments?: boolean | null;
    receivedAfter?: string | null;
    receivedBefore?: string | null;
  };
}

// Export singleton class for external access
export class EmailStateManager {
  private static instance: EmailStateManager;
  private subscribers: Set<Function> = new Set();
  private state = {
    emails: [] as Email[],
    loading: false,
    error: null as string | null,
    nextCursor: null as string | null,
    initialized: false
  };
  
  private lastFetchParams: string = '';
  private requestInProgress = false;
  private persistentInitialized = false; // NEW: Persistent initialized flag
  private currentFolder: EmailFolder | null = null; // Track current folder to validate nextCursor
  private lastStateSnapshot: { emails: Email[]; loading: boolean; error: string | null; nextCursor: string | null; initialized: boolean } | null = null;
  private lastSnapshotKey: string = '';
  private stateVersion = 0;
  private incrementalFetchInProgress = false;
  
  static getInstance() {
    if (!EmailStateManager.instance) {
      EmailStateManager.instance = new EmailStateManager();
    }
    return EmailStateManager.instance;
  }
  
  subscribe(callback: Function) {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }
  
  private notify() {
    this.subscribers.forEach(callback => callback(this.getState()));
  }
  
  updateState(updates: Partial<typeof this.state>) {
    // Shallow compare — only notify when something actually changed
    const keys = Object.keys(updates) as Array<keyof typeof this.state>;
    const hasChange = keys.some(k => (updates as any)[k] !== (this.state as any)[k]);
    if (!hasChange) return; // Bail out: no new object, no notify, no re-render

    this.state = { ...this.state, ...updates };
    if (updates.initialized !== undefined) {
      this.persistentInitialized = updates.initialized;
    }
    this.stateVersion++;
    this.notify();
  }
  
  async fetchEmails(options: UseEmailManagerOptions, reset = false, trigger = 'unknown', orgId?: string) {
    console.log('[EMAIL FETCH]', trigger, new Date().toISOString());
    const { folder = 'inbox', limit = 50, accountId, filters = {}, refreshFromNylas = false } = options;

    if (!orgId) {
      this.updateState({ error: 'Organization ID is required', loading: false });
      return;
    }
    
    // Verificar se a pasta mudou - se mudou, SEMPRE fazer reset e forçar fetch
    const folderChanged = this.currentFolder !== null && this.currentFolder !== folder;
    if (folderChanged) {
      // Forçar reset quando pasta muda
      reset = true;
      // Se há uma requisição em progresso, cancelar (permitir nova requisição)
      if (this.requestInProgress) {
        this.requestInProgress = false;
      }
      // Limpar estado imediatamente para evitar mostrar emails da pasta anterior
      // IMPORTANTE: Limpar ANTES de atualizar lastFetchParams para evitar cache
      // MAS: manter initialized para não mostrar mensagem de sincronização
      this.state.emails = [];
      this.state.nextCursor = null;
      this.lastFetchParams = ''; // Limpar para forçar novo fetch
      this.currentFolder = folder; // Atualizar pasta atual ANTES de fazer fetch
      // IMPORTANTE: Manter initialized se já foi inicializado (usar persistentInitialized como fallback)
      const shouldKeepInitialized = this.state.initialized || this.persistentInitialized;
      // Notificar imediatamente para atualizar a UI, mas manter initialized
      this.updateState({ 
        emails: [], 
        nextCursor: null, 
        loading: true,
        initialized: shouldKeepInitialized // Sempre manter se já foi inicializado
      });
    }
    
    // Create params signature to prevent duplicate calls (sem reset para comparação)
    const currentParams = JSON.stringify({ folder, limit, accountId, filters, refreshFromNylas });
    
    // Se os parâmetros são os mesmos e já temos emails carregados, não fazer fetch novamente
    // MAS: só se não for reset e a pasta não mudou
    if (!reset && !folderChanged && this.lastFetchParams === currentParams && this.state.emails.length > 0 && this.persistentInitialized) {
      return;
    }
    
    if (!reset && !folderChanged && this.lastFetchParams === currentParams && this.requestInProgress) {
      return;
    }
    
    // Se há requisição em progresso e não é mudança de pasta, bloquear
    // MAS: se é mudança de pasta ou reset explícito, sempre permitir
    if (this.requestInProgress && !trigger.includes('force') && !folderChanged && !reset) {
      return;
    }
    
    this.requestInProgress = true;
    // Atualizar lastFetchParams apenas com parâmetros sem reset
    this.lastFetchParams = currentParams;
    this.updateState({ loading: true, error: null });
    
    // Validar que nextCursor pertence à pasta atual antes de usar
    // Se a pasta mudou ou é reset, não usar nextCursor
    let currentPageToken: string | null = null;
    if (!reset && !folderChanged && this.state.nextCursor) {
      if (this.currentFolder === folder) {
        // Pasta é a mesma, pode usar nextCursor
        currentPageToken = this.state.nextCursor;
      } else {
        // Pasta mudou, não usar nextCursor de pasta anterior
        this.state.nextCursor = null; // Resetar nextCursor quando pasta muda
        currentPageToken = null; // Forçar busca do início
      }
    } else {
      // Se é reset ou pasta mudou, garantir que nextCursor está null
      this.state.nextCursor = null;
      currentPageToken = null;
    }
    
    // Atualizar pasta atual ANTES de fazer a requisição (se ainda não foi atualizada)
    if (!folderChanged) {
      this.currentFolder = folder;
    }
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        this.updateState({ loading: false, error: 'Sessão expirada. Faça login novamente.' });
        this.requestInProgress = false;
        return;
      }

      const { data, error } = await supabase.functions.invoke('nylas-list-emails', {
        body: {
          org_id: orgId,
          account_id: accountId,
          folder,
          limit,
          page_token: currentPageToken,
          search: filters.search || '',
          unread: filters.unread,
          has_attachments: filters.hasAttachments,
          received_after: filters.receivedAfter,
          received_before: filters.receivedBefore,
          refresh_from_nylas: refreshFromNylas === true,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('[EMAIL MANAGER] API ERROR', error);
        
        // Handle token refresh if needed
        if (error.needsRefresh && error.accountId) {
          try {
            const { error: refreshError } = await supabase.functions.invoke('nylas-refresh-token', {
              body: { accountId: error.accountId }
            });
            
            if (!refreshError) {
              this.requestInProgress = false;
              return this.fetchEmails(options, reset, `${trigger}-retry-after-refresh`);
            }
          } catch (refreshErr) {
            console.error('[EMAIL MANAGER] Token refresh failed', refreshErr);
          }
        }
        
        throw error;
      }

      const rawList = data.data || [];
      const newEmails: Email[] = rawList.map((raw: Record<string, unknown>) => ({
        ...(raw as unknown as Email),
        id: String(raw.id ?? ''),
        date: raw.date ? new Date(raw.date as string | number | Date) : new Date(),
        body: typeof raw.body === 'string' ? raw.body : '',
        snippet: typeof raw.snippet === 'string' ? raw.snippet : undefined,
        emailMessageId: typeof raw.emailMessageId === 'string' ? raw.emailMessageId : undefined,
        accountId: typeof raw.accountId === 'string' ? raw.accountId : undefined,
        accountEmail: typeof raw.accountEmail === 'string' ? raw.accountEmail : undefined,
      }));
      
      if (reset) {
        // IMPORTANTE: Quando reset = true, SEMPRE substituir completamente os emails
        // Garantir que o estado está limpo antes de adicionar novos emails
        // Limpar emails ANTES de processar novos para evitar mistura
        this.state.emails = [];
        
        // Garantir que os emails estão ordenados do mais recente para o mais antigo
        const sortedEmails = [...newEmails].sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateB - dateA; // Mais recente primeiro
        });
        
        // Garantir que todos os emails pertencem à pasta atual
        // Se o email não tem propriedade folder, assumir que pertence à pasta atual (compatibilidade)
        // Mas se tem e não corresponde, filtrar
        const emailsForCurrentFolder = sortedEmails.filter(email => {
          // Se o email tem propriedade folder definida, validar que corresponde à pasta atual
          if (email.folder !== undefined && email.folder !== null) {
            const emailFolder = String(email.folder).toLowerCase();
            const currentFolderLower = folder.toLowerCase();
            if (emailFolder !== currentFolderLower) {
              return false;
            }
          }
          // Se não tem propriedade folder, manter (pode ser email antigo sem essa propriedade)
          // Mas vamos adicionar a propriedade folder para garantir consistência
          if (!email.folder) {
            (email as any).folder = folder;
          }
          return true;
        });
        
        // SEMPRE substituir completamente os emails quando reset = true
        // Garantir que apenas emails da pasta atual sejam mantidos
        // IMPORTANTE: Sempre marcar como inicializado após buscar emails (mesmo que retorne 0 emails)
        // Isso evita mostrar mensagem de sincronização quando a pasta está vazia
        const shouldKeepInitialized = this.state.initialized || this.persistentInitialized;
        this.updateState({
          emails: emailsForCurrentFolder, // Usar apenas emails da pasta atual
          nextCursor: data.next_cursor || null,
          initialized: true, // Sempre true após buscar emails com sucesso (mesmo que retorne 0 emails)
          loading: false
        });
      } else {
        // Filtrar emails existentes para garantir que pertencem à pasta atual
        const existingEmailsForFolder = this.state.emails.filter(email => {
          // Se o email tem propriedade folder definida, validar que corresponde à pasta atual
          if (email.folder !== undefined && email.folder !== null) {
            const emailFolder = String(email.folder).toLowerCase();
            const currentFolderLower = folder.toLowerCase();
            if (emailFolder !== currentFolderLower) {
              return false;
            }
          }
          // Se não tem propriedade folder, assumir que pertence à pasta atual e adicionar
          if (!email.folder) {
            (email as any).folder = folder;
          }
          return true;
        });
        
        // Combinar apenas emails da pasta atual com novos emails
        const combinedEmails = [...existingEmailsForFolder, ...newEmails];
        combinedEmails.sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateB - dateA; // Mais recente primeiro
        });
        
        // Remover duplicatas baseado no ID
        const uniqueEmails = combinedEmails.filter((email, index, self) =>
          index === self.findIndex(e => e.id === email.id)
        );
        
        // Validar que todos os emails pertencem à pasta atual
        const validatedEmails = uniqueEmails.filter(email => {
          // Se o email tem propriedade folder definida, validar que corresponde à pasta atual
          if (email.folder !== undefined && email.folder !== null) {
            const emailFolder = String(email.folder).toLowerCase();
            const currentFolderLower = folder.toLowerCase();
            if (emailFolder !== currentFolderLower) {
              return false;
            }
          }
          // Se não tem propriedade folder, adicionar para garantir consistência
          if (!email.folder) {
            (email as any).folder = folder;
          }
          return true;
        });
        
        // IMPORTANTE: Sempre marcar como inicializado após buscar emails (mesmo que retorne 0 emails)
        // Isso evita mostrar mensagem de sincronização quando a pasta está vazia
        this.updateState({
          emails: validatedEmails, // Usar apenas emails validados da pasta atual
          nextCursor: data.next_cursor || null,
          initialized: true, // Sempre true após buscar emails com sucesso (mesmo que retorne 0 emails)
          loading: false
        });
      }
      
    } catch (err: any) {
      console.error('[EMAIL MANAGER] FAILED', err);
      const errorMessage = err.message || 'Erro ao carregar emails';
      this.updateState({ error: errorMessage, loading: false });
      toast.error(errorMessage);
    } finally {
      this.requestInProgress = false;
    }
  }
  
  getState() {
    // Version-based cache: only creates new object when stateVersion increments
    const key = `v${this.stateVersion}`;
    if (this.lastStateSnapshot && this.lastSnapshotKey === key) {
      return this.lastStateSnapshot;
    }
    this.lastSnapshotKey = key;
    this.lastStateSnapshot = { ...this.state, initialized: this.persistentInitialized };
    return this.lastStateSnapshot;
  }
  
  // Atualização otimista - atualizar email localmente sem recarregar
  updateEmailLocally(emailId: string, updates: Partial<Email>) {
    let changed = false;
    const newEmails = this.state.emails.map(email => {
      if (email.id === emailId) {
        const needsUpdate = Object.keys(updates).some(
          k => (email as any)[k] !== (updates as any)[k]
        );
        if (needsUpdate) { changed = true; return { ...email, ...updates }; }
      }
      return email;
    });
    if (!changed) return;
    this.updateState({ emails: newEmails });
  }

  // Remoção otimista - remover email localmente
  removeEmailLocally(emailId: string) {
    if (!this.state.emails.some(e => e.id === emailId)) return;
    this.updateState({ emails: this.state.emails.filter(e => e.id !== emailId) });
  }

  // Restaurar email removido (rollback em caso de erro)
  restoreEmailLocally(email: Email) {
    if (this.state.emails.some(e => e.id === email.id)) return;
    this.updateState({
      emails: [...this.state.emails, email]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    });
  }
  
  // Buscar APENAS novos emails (incremental refresh — silent, no loading toggle)
  async fetchNewEmailsOnly(options: UseEmailManagerOptions, orgId?: string) {
    console.log('[EMAIL INCREMENTAL FETCH]', new Date().toISOString());
    const { folder = 'inbox', accountId, filters = {} } = options;

    // Se não há emails ou orgId, fazer busca completa
    if (!orgId || this.state.emails.length === 0) {
      return this.fetchEmails(options, true, 'fetch-new-no-existing', orgId);
    }

    // Concurrency guard — prevent overlapping incremental fetches
    if (this.incrementalFetchInProgress) return;
    this.incrementalFetchInProgress = true;

    try {
      // Só usar received_after para inbox - outras pastas devem buscar todos os emails
      const shouldUseReceivedAfter = folder === 'inbox';

      let receivedAfter: string | null = null;
      if (shouldUseReceivedAfter) {
        const mostRecentEmail = this.state.emails[0];
        receivedAfter = mostRecentEmail?.date
          ? new Date(mostRecentEmail.date).toISOString()
          : null;

        if (!receivedAfter) {
          return this.fetchEmails(options, true, 'fetch-new-no-date', orgId);
        }

        // Validar que a data não é futura
        const receivedAfterDate = new Date(receivedAfter);
        const now = new Date();
        if (receivedAfterDate > now) {
          receivedAfter = null;
        }
      }

      // No loading toggle — this is a silent background refresh

      const requestBody: any = {
        org_id: orgId,
        account_id: accountId,
        folder,
        limit: 20,
        ...filters
      };

      if (receivedAfter) {
        requestBody.received_after = receivedAfter;
      }

      const { data, error } = await supabase.functions.invoke('nylas-list-emails', {
        body: requestBody
      });

      if (error) throw error;

      const newEmails = data?.data || [];

      if (newEmails.length > 0) {
        const existingIds = new Set(this.state.emails.map(e => e.id));
        const uniqueNewEmails = newEmails.filter((e: Email) => !existingIds.has(e.id));

        if (uniqueNewEmails.length > 0) {
          this.updateState({
            emails: [...uniqueNewEmails, ...this.state.emails]
          });
        }
        // No updateState if no unique emails — nothing changed
      }
      // No updateState if no new emails — nothing changed
    } catch (err: any) {
      console.error('[EMAIL MANAGER] Error fetching new emails:', err);
      this.updateState({ error: (err as Error).message });
    } finally {
      this.incrementalFetchInProgress = false;
    }
  }
  
  reset() {
    const wasInitialized = this.state.initialized || this.persistentInitialized;
    this.state = {
      emails: [],
      loading: false,
      error: null,
      nextCursor: null,
      initialized: wasInitialized
    };
    this.lastFetchParams = '';
    this.requestInProgress = false;
    this.incrementalFetchInProgress = false;
    this.currentFolder = null;
    this.stateVersion++;
    this.notify();
  }
  
  resetAll() {
    this.reset();
    this.persistentInitialized = false;
    this.stateVersion++;
    this.notify();
  }
}

/**
 * RADICAL APPROACH: Single source of truth for email state
 * NO circular dependencies, NO automatic triggers
 */
export const useEmailManager = (options: UseEmailManagerOptions = {}, orgId?: string) => {
  const manager = EmailStateManager.getInstance();

  // useSyncExternalStore: React only re-renders when getState() returns a different reference
  const subscribeToManager = useCallback(
    (onStoreChange: () => void) => manager.subscribe(onStoreChange),
    []
  );
  const state = useSyncExternalStore(subscribeToManager, () => manager.getState());

  // Stabilize options via ref — only update when values actually change
  const optionsRef = useRef(options);
  const orgIdRef = useRef(orgId);
  const optionsKey = useMemo(() => JSON.stringify(options), [
    options.folder, options.limit, options.accountId, options.refreshFromNylas,
    options.filters?.search, options.filters?.unread, options.filters?.hasAttachments,
    options.filters?.receivedAfter, options.filters?.receivedBefore,
  ]);
  const prevOptionsKeyRef = useRef(optionsKey);
  if (prevOptionsKeyRef.current !== optionsKey) {
    prevOptionsKeyRef.current = optionsKey;
    optionsRef.current = options;
  }
  orgIdRef.current = orgId;

  // All callbacks use refs — stable identity, always fresh values
  const fetchEmails = useCallback(
    (trigger: string, reset = false, overrides?: Partial<UseEmailManagerOptions>) => {
      return manager.fetchEmails({ ...optionsRef.current, ...overrides }, reset, trigger, orgIdRef.current);
    },
    [],
  );

  const refreshEmails = useCallback(
    (trigger: string) => manager.fetchEmails(optionsRef.current, true, trigger, orgIdRef.current),
    [],
  );

  const refreshEmailsFromNylas = useCallback(
    (trigger: string) => manager.fetchEmails({ ...optionsRef.current, refreshFromNylas: true }, true, trigger, orgIdRef.current),
    [],
  );

  const loadMoreEmails = useCallback(
    (trigger: string) => {
      const s = manager.getState();
      if (!s.loading && s.nextCursor) {
        return manager.fetchEmails(optionsRef.current, false, trigger, orgIdRef.current);
      }
    },
    [],
  );

  const resetState = useCallback(() => {
    manager.reset();
  }, []);

  const updateEmailLocally = useCallback((emailId: string, updates: Partial<Email>) => {
    manager.updateEmailLocally(emailId, updates);
  }, []);

  const removeEmailLocally = useCallback((emailId: string) => {
    manager.removeEmailLocally(emailId);
  }, []);

  const restoreEmailLocally = useCallback((email: Email) => {
    manager.restoreEmailLocally(email);
  }, []);

  const refreshNewEmailsOnly = useCallback((trigger: string) => {
    return manager.fetchNewEmailsOnly(optionsRef.current, orgIdRef.current);
  }, []);

  // Return value only changes when state data changes (functions are stable)
  return useMemo(() => ({
    emails: state.emails,
    loading: state.loading,
    error: state.error,
    hasMore: !!state.nextCursor,
    initialized: state.initialized,
    fetchEmails,
    refreshEmails,
    refreshEmailsFromNylas,
    loadMoreEmails,
    resetState,
    updateEmailLocally,
    removeEmailLocally,
    restoreEmailLocally,
    refreshNewEmailsOnly
  }), [
    state.emails,
    state.loading,
    state.error,
    state.nextCursor,
    state.initialized,
    fetchEmails,
    refreshEmails,
    refreshEmailsFromNylas,
    loadMoreEmails,
    resetState,
    updateEmailLocally,
    removeEmailLocally,
    restoreEmailLocally,
    refreshNewEmailsOnly
  ]);
};